import { useEffect, useRef, useCallback, useState } from 'react';
import { POI, PlannerPoi } from 'components/common/types/Planner.types';
import { Asset } from 'components/common/types/Asset.types';
import { AssetsInRangeResult, POICluster } from '../POIClusters.types';

export interface UsePOIWorkerReturn {
  generateClusters: (poiCategories: POI[]) => Promise<POICluster[]>;
  generateDirectSalesClusters: (poiPoints: PlannerPoi[]) => Promise<POICluster[]>;
  getAssetsInRange: (
    assets: Asset[],
    poiClusters: POICluster[],
    enablePrecisionCalculations?: boolean,
  ) => Promise<AssetsInRangeResult[]>;
  isReady: boolean;
}

type PendingResolverValue = POICluster[] | PromiseLike<POICluster[]>;
type PendingResolver = (value: PendingResolverValue) => void;

export const usePOIWorker = (): UsePOIWorkerReturn => {
  const workerRef = useRef<Worker | null>(null);
  const pendingResolversRef = useRef<Map<string, PendingResolver>>(new Map());
  const requestIdRef = useRef(0);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    let worker: Worker | null = null;

    try {
      worker = new Worker(new URL('./poi.worker.ts', import.meta.url), { type: 'module' });

      worker.onmessage = (e: MessageEvent) => {
        const { type, payload, requestId } = e.data;

        if (
          (type === 'CLUSTERS_GENERATED' ||
            type === 'DIRECT_SALES_CLUSTERS_GENERATED' ||
            type === 'ASSETS_IN_RANGE_RESULT') &&
          requestId
        ) {
          const resolver = pendingResolversRef.current.get(requestId);
          if (resolver) {
            resolver(payload);
            pendingResolversRef.current.delete(requestId);
          }
        }
      };

      worker.onerror = () => {
        pendingResolversRef.current.forEach((resolve) => resolve([]));
        pendingResolversRef.current.clear();
      };

      workerRef.current = worker;
      setIsReady(true);
    } catch (error) {
      setIsReady(false);
    }

    return () => {
      if (worker) {
        worker.terminate();
        workerRef.current = null;
        setIsReady(false);
      }
      pendingResolversRef.current.clear();
    };
  }, []);

  const generateClusters = useCallback(
    (poiCategories: POI[]): Promise<POICluster[]> => {
      return new Promise((resolve) => {
        if (!workerRef.current || !isReady) {
          resolve([]);
          return;
        }

        const requestId = `req_${requestIdRef.current++}`;
        pendingResolversRef.current.set(requestId, resolve);

        try {
          workerRef.current.postMessage({
            type: 'GENERATE_CLUSTERS',
            payload: { poiCategories },
            requestId,
          });
        } catch (error) {
          pendingResolversRef.current.delete(requestId);
          resolve([]);
        }
      });
    },
    [isReady],
  );

  const generateDirectSalesClusters = useCallback(
    (poiPoints: PlannerPoi[]): Promise<POICluster[]> => {
      return new Promise((resolve) => {
        if (!workerRef.current || !isReady) {
          resolve([]);
          return;
        }

        const requestId = `req_${requestIdRef.current++}`;
        pendingResolversRef.current.set(requestId, resolve);

        try {
          workerRef.current.postMessage({
            type: 'GENERATE_DIRECT_SALES_CLUSTERS',
            payload: { poiPoints },
            requestId,
          });
        } catch (error) {
          pendingResolversRef.current.delete(requestId);
          resolve([]);
        }
      });
    },
    [isReady],
  );

  const getAssetsInRange = useCallback(
    (
      assets: Asset[],
      poiClusters: POICluster[],
      enablePrecisionCalculations: boolean = true,
    ): Promise<AssetsInRangeResult[]> => {
      return new Promise((resolve) => {
        if (!workerRef.current || !isReady) {
          resolve([]);
          return;
        }

        const requestId = `req_${requestIdRef.current++}`;
        pendingResolversRef.current.set(requestId, resolve as unknown as PendingResolver);

        try {
          workerRef.current.postMessage({
            type: 'GET_ASSETS_IN_RANGE',
            payload: { assets, poiClusters, enablePrecisionCalculations },
            requestId,
          });
        } catch (error) {
          pendingResolversRef.current.delete(requestId);
          resolve([]);
        }
      });
    },
    [isReady],
  );

  return {
    generateClusters,
    generateDirectSalesClusters,
    getAssetsInRange,
    isReady,
  };
};
